import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import AiBadgeManager from "@/src/components/AiBadgeManager.tsx";
import { useUserSettings } from "@/src/hooks/useUserSettings.ts";

/**
 * /settings/badge — the "AI-generated" badge settings page. Hosts the on/off
 * default for downloads plus the badge library manager (styled pills and
 * uploaded label files, SIS-481). Reached from the settings nav and from the
 * gear next to the badge checkbox in the download menus / marketplace export
 * dialog.
 */
const BadgeSettings: React.FC = () => {
  const { t } = useTranslation();
  const { aiWatermarkEnabled, setAiWatermarkEnabled, isLoading } =
    useUserSettings();
  // While a badge editor is open, the download on/off toggle is unrelated
  // noise — hide it so the editor gets the full card (and the save button
  // stays above the fold on laptop-height viewports).
  const [isEditing, setIsEditing] = React.useState(false);

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t("studio.aiWatermarkDesignerTitle")}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {t("studio.aiWatermarkDesignerSubtitle")}
        </p>
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
          {!isEditing && (
            <button
              type="button"
              role="switch"
              aria-checked={aiWatermarkEnabled}
              disabled={isLoading}
              onClick={() => setAiWatermarkEnabled(!aiWatermarkEnabled)}
              className="w-full flex items-center justify-between gap-3 cursor-pointer group text-left"
            >
              <span className="text-sm font-medium text-gray-700 group-hover:text-black transition-colors">
                {t("studio.aiWatermarkOption")}
              </span>
              <div
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${aiWatermarkEnabled ? "bg-black" : "bg-gray-200"}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${aiWatermarkEnabled ? "translate-x-4" : "translate-x-0"}`}
                />
              </div>
            </button>
          )}

          <div className={isEditing ? "" : "pt-4 border-t border-gray-100"}>
            <AiBadgeManager onEditingChange={setIsEditing} />
          </div>
        </div>
      </section>
    </div>
  );
};

export const Route = createFileRoute("/settings/badge")({
  component: BadgeSettings,
});
